// The Newton handoff payload. In the two-stage demo, the team runs the full
// Newton framework LIVE in Newton (sources of growth → hypothesis → audience →
// comms → investment scenarios) and then comes to this Elevate page to continue
// with streaming line-item planning + buying. This is what Newton "hands off":
// the selected hypothesis, the audience, the Get/To/By communications strategy,
// the chosen investment scenario, and the streaming/video budget the planner
// starts from — split by buy path (CTV/direct-IO vs. OLV/Nexxen programmatic).
//
// CAPTURED: these are the real figures from the team's Newton Bliss Point Planner
// run (conversation 1321, project 42) — Balanced Awareness & Sales scenario
// ($8M total, $2M/mo × 4). Cognitive-Performance Consumers 25–54.
// REFRESHED 2026-08-19 from the updated conv 1321: flight Apr 25 – Aug 14 2027;
// Balanced streaming/video slice = CTV (Streaming/AVOD) $3.12M + OLV (Online video)
// $640K. Of the $3.12M CTV, $1.5M is the assumed Netflix Women's World Cup streaming
// sponsorship (worldCupHoldout, concentrated in the weeks 9–12 burst) — shown in the
// intake but held out of the planner; the remaining ~$150K of that burst plus the
// build/sustain weeks (plannable CTV $1.62M) is planned by the tool. The 16-week
// build → WWC burst → sustain flighting (below) is passed to the agent to build against.

export interface HandoffAudience {
  name: string;
  size: number;
  note: string;
}

export interface HandoffScenario {
  key: 'A' | 'B' | 'C';
  label: string;
  emphasis: string;
  chosen: boolean;
}

// One phase of the weekly flighting pulse (build → WWC burst → sustain) from the
// conv 1321 flighting run. ctvSpend/olvSpend are the tool-planned streaming spend in
// that phase (CTV is net of the held-out Netflix WWC sponsorship in the burst phase).
export interface FlightingPhase {
  label: string;
  weeks: string;
  dates: string;
  ctvSpend: number;
  olvSpend: number;
  note: string;
}

export interface NewtonHandoff {
  selectedHypothesis: string;
  hypothesisLabel: string;
  primaryAudience: HandoffAudience;
  priorityCohort: HandoffAudience;
  briefAudience: HandoffAudience;
  commsStrategy: { get: string; to: string; by: string };
  // Launch markets from the brief + the market modeled in detail, and the flight.
  markets: string[];
  detailMarket: string;
  // Human-readable flight window plus machine-usable start/end dates (ISO) so the
  // planner drafts every line item to this exact window.
  flight: string;
  flightStart: string;
  flightEnd: string;
  scenarios: HandoffScenario[];
  chosenScenarioKey: 'A' | 'B' | 'C';
  // CTV budget — the Streaming/AVOD slice of the chosen scenario, bought as
  // CTV direct-IO lines. This is the figure SHOWN in the intake; the plannable
  // amount handed to the agent is (ctvBudget − worldCupHoldout). Part of the
  // planner's starting media budget.
  ctvBudget: number;
  // OLV budget — the Online-video slice of the chosen scenario, bought as
  // Nexxen programmatic OLV lines. Combined with ctvBudget for the total.
  olvBudget: number;
  // Reserved CTV sponsorship — the Netflix Women's World Cup streaming sponsorship.
  // DISPLAYED as part of ctvBudget in the intake, but HELD OUT of the agent's
  // plannable budget: handoffContext() subtracts this from ctvBudget so the planner
  // drafts direct-IO lines against (ctvBudget − worldCupHoldout) only. The reserved
  // amount is handled separately and the agent must draft nothing against it.
  worldCupHoldout: number;
  // Provisional demo grounding assumptions handed to the planner. Stated as
  // assumptions (may be replaced by a real Newton pull later) so the planner
  // draws audience, creative, and inventory within these bounds.
  planningAssumptions: string[];
  // Streaming networks to request, in the MRI-priority order from the Newton run
  // (conv 1321). Handed to the planner as preferred networks; not rendered in the UI.
  networkPriority: string[];
  // Nexxen behavioral segment SETS to apply to the OLV (Nexxen) lines ONLY — one per
  // line. Names must match the agent's configured audiences exactly. CTV / direct-IO
  // lines carry no audience in this demo.
  olvAudiences: string[];
  // Primary KPI the planner optimizes toward, plus the campaign goals (JTBD framing).
  primaryKpi: string;
  goals: string;
  // Channel history — none; ELEVATE is a new brand launch (no incumbent networks).
  channelHistory: string;
  // Weekly flighting pulse (conv 1321): build → Women's World Cup burst → sustain
  // across the 16-week flight. Passed to the agent so it builds the plan to this
  // pacing (populates the plan's flighting_strategy), and shown in the intake.
  flighting: FlightingPhase[];
}

export const NEWTON_HANDOFF: NewtonHandoff = {
  hypothesisLabel: 'Format migration',
  selectedHypothesis:
    'Consumers already seeking functional-wellness and cognitive-performance benefits will adopt ' +
    'ELEVATE as a convenient, ready-to-drink beverage expression of that routine.',
  primaryAudience: {
    name: 'Cognitive-Performance Consumers 25–54',
    size: 23_153_550,
    note:
      'MRI-constructed: energy / sports-drink or bottled-water users who agree with health, ' +
      'wellness, nutrition, natural-product, or efficiency statements, ages 25–54.'
  },
  priorityCohort: {
    name: 'Urban Millennial Professionals with strong cognitive-performance fit',
    size: 2_249_280,
    note:
      'The brief’s intended cohort, narrowed to those with high functional-beverage fit — the ' +
      'priority launch audience within the broader opportunity.'
  },
  briefAudience: {
    name: 'Urban Millennial Professionals',
    size: 0,
    note:
      'The audience named in the Forrester brief; validated and refined by the hypothesis ' +
      'audience rather than replaced.'
  },
  commsStrategy: {
    get:
      'Functional-energy consumers and wellness-oriented professionals already seeking ' +
      'cognitive, stress-management, or performance benefits',
    to: 'Adopt ELEVATE as a convenient, credible way to access those benefits in everyday life',
    by:
      'Showing that ELEVATE delivers low-sugar, smooth cognitive energy through understandable ' +
      'functional ingredients in an easy ready-to-drink format'
  },
  scenarios: [
    {
      key: 'A',
      label: 'Awareness Maximization',
      emphasis:
        'Broad-reach video, streaming & YouTube, paid social, creators — mental availability at scale.',
      chosen: false
    },
    {
      key: 'B',
      label: 'Balanced Awareness & Sales',
      emphasis:
        'Video & social for reach, creators for credibility, search + retail/commerce for trial and repeat.',
      chosen: true
    },
    {
      key: 'C',
      label: 'Short-Term Sales',
      emphasis:
        'Retail & commerce media, paid search, performance social, retargeting — fastest sales proof.',
      chosen: false
    }
  ],
  markets: ['United States'],
  detailMarket: 'United States',
  flight: 'Apr 25, 2027 – Aug 14, 2027',
  flightStart: '2027-04-25',
  flightEnd: '2027-08-14',
  chosenScenarioKey: 'B',
  ctvBudget: 3_120_000,
  olvBudget: 640_000,
  worldCupHoldout: 1_500_000,
  planningAssumptions: [
    'No first-party (1P) data available — build audience from MRI / 3P segments only (no 1P retargeting or suppression).',
    'Creative assets: :30 second video only.',
    'No major inventory restrictions and no brand-safety concerns above normal levels — no site/app allow-list or exclusions beyond standard brand-safety controls.'
  ],
  networkPriority: [
    'Netflix',
    'Prime Video',
    'Hulu',
    'Disney+',
    'Paramount+',
    'Peacock',
    'Tubi',
    'Roku Channel',
    'ESPN+',
    'Pluto TV'
  ],
  olvAudiences: ['Urban Millennials', 'High Income'],
  primaryKpi: 'visits',
  goals:
    'Awareness + Purchase Intent (JTBD): build launch awareness while driving trial and purchase intent.',
  channelHistory:
    'None — ELEVATE is a new brand launch; no incumbent networks or prior channel history.',
  flighting: [
    {
      label: 'Build',
      weeks: 'Weeks 1–8',
      dates: 'Apr 25 – Jun 19, 2027',
      ctvSpend: 1_020_000,
      olvSpend: 268_800,
      note: 'Weekly spend ramps up gradually.'
    },
    {
      label: 'Women’s World Cup burst',
      weeks: 'Weeks 9–12',
      dates: 'Jun 20 – Jul 17, 2027',
      ctvSpend: 150_000,
      olvSpend: 236_800,
      note: 'Heaviest concentration, anchored by the Netflix Women’s World Cup streaming sponsorship (the $1.5M sponsorship is held out of the tool; this is the remaining tool-planned CTV in the window).'
    },
    {
      label: 'Sustain',
      weeks: 'Weeks 13–16',
      dates: 'Jul 18 – Aug 14, 2027',
      ctvSpend: 450_000,
      olvSpend: 134_400,
      note: 'Spend steps down but stays active through flight end.'
    }
  ]
};
